import { dec, HOP_FORM_LABELS, ZERO } from "@bl/domain";
import { Plus, Search } from "lucide-react";
import { type KeyboardEvent, useEffect, useState } from "react";
import { type CatalogProduct, type Item, send, useCatalog, useChange, useItems } from "../api";
import { useMedia } from "../components/DocumentViewer";
import { ErrorNote } from "../components/ui";
import { categoryLabel, formLabel, hopDetail, itemChoice, showOnHand } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { useNotify } from "../notify";

/** How many of the brewery's own items show at once; typing narrows the rest. */
const SHOWN = 8;
/**
 * On a touch screen the search is not focused, so the keyboard does not cover the list (the
 * re-critique, 2026-10-06), and more of the list shows for a thumb to pick from without typing.
 */
const SHOWN_ON_TOUCH = 40;

/**
 * Choosing an item by searching for it rather than from a list of every item (Scott, 2026-10-02:
 * "a search menu instead of just a drop down of every item"). As in the recipe builder, what is on
 * hand shows before anything is typed; typing narrows it by name, maker, form, SKU or category, and
 * adds what the catalog has that the brewery does not stock yet, which one press adds to inventory.
 *
 * New item hands back what was typed, for the Add item dialog. That dialog is a form, so whoever
 * holds this field opens it outside their own form: a form inside a form submits both.
 */
export function ItemPicker({
  id,
  "aria-describedby": describedBy,
  onPick,
  onCreate,
}: {
  id?: string;
  "aria-describedby"?: string;
  onPick: (item: Item) => void;
  onCreate: (query: string) => void;
}) {
  const touch = useMedia("(pointer: coarse)");
  const [typed, setTyped] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQuery(typed), 150);
    return () => clearTimeout(t);
  }, [typed]);
  // The row Enter takes, moved by the arrow keys; -1 until they are used.
  const [active, setActive] = useState(-1);
  const { data: items = [] } = useItems();
  const { data: catalog = [] } = useCatalog(query, null);
  const { layout } = useLayout();
  const notify = useNotify();
  const adopt = useChange(send.item, {
    onSuccess: (item) => {
      notify({
        title: `${item.displayName} added to your inventory`,
        link: { kind: "item", itemId: item.id },
      });
      onPick(item);
    },
  });

  const words = typed.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const stocked = (i: Item) => dec(i.onHand.quantity).gt(ZERO);
  const matches = items
    .filter((i) => {
      // With its form and brand, since a hop is named for its variety alone: two Citras read "Citra".
      const text = [
        itemChoice(i),
        i.manufacturer,
        formLabel(i),
        i.defaultSupplierSku,
        categoryLabel(i.category),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return words.every((w) => text.includes(w));
    })
    .sort(
      (a, b) =>
        Number(stocked(b)) - Number(stocked(a)) || a.displayName.localeCompare(b.displayName),
    );
  const shown = touch ? SHOWN_ON_TOUCH : SHOWN;
  const mine = matches.slice(0, shown);
  // The catalog only once two letters are typed, and never the last search's results after clearing.
  const fromCatalog =
    words.length && query.trim().length >= 2
      ? catalog.filter((p) => p.adoptedItemIds.length === 0).slice(0, 6)
      : [];
  const add = (p: CatalogProduct) => adopt.mutate({ source: "catalog", catalogProductId: p.id });
  const rows = [...mine.map((i) => () => onPick(i)), ...fromCatalog.map((p) => () => add(p))];
  const marked =
    rows.length === 0
      ? -1
      : active >= 0
        ? Math.min(active, rows.length - 1)
        : words.length
          ? 0
          : -1;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && rows.length) {
      e.preventDefault();
      setActive(marked + 1 >= rows.length ? 0 : marked + 1);
    } else if (e.key === "ArrowUp" && rows.length) {
      e.preventDefault();
      setActive(marked <= 0 ? rows.length - 1 : marked - 1);
    } else if (e.key === "Enter") {
      // Never the dialog's own submit: Enter here means the row marked, if there is one.
      e.preventDefault();
      if (marked >= 0 && !adopt.isPending) rows[marked]?.();
    }
  };

  return (
    <div className="item-picker">
      <div className="picker-search">
        <Search size={16} aria-hidden />
        <input
          id={id}
          aria-describedby={describedBy}
          className="input"
          type="search"
          autoComplete="off"
          placeholder="Search items and the catalog"
          value={typed}
          // biome-ignore lint/a11y/noAutofocus: the item is the first thing the dialog asks for
          autoFocus={!touch}
          onChange={(e) => {
            setTyped(e.target.value);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      <ErrorNote error={adopt.error} />
      <h3 className="item-picker-heading">In your inventory</h3>
      {mine.length === 0 ? (
        <p className="faint">
          {words.length
            ? "Nothing in your inventory by that name."
            : "Nothing in your inventory yet."}
        </p>
      ) : (
        <ul className="picker-list">
          {mine.map((it, i) => (
            <li key={it.id}>
              <button
                type="button"
                className={i === marked ? "active" : ""}
                onClick={() => onPick(it)}
              >
                <span>
                  {it.displayName}
                  {hopDetail(it) ? <span className="faint"> · {hopDetail(it)}</span> : null}
                </span>
                <span className="faint num">
                  {stocked(it)
                    ? `${showOnHand(it, layout.inventory.quantityMode)} on hand`
                    : "none on hand"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {matches.length > shown ? (
        <p className="faint">{matches.length - shown} more. Type to narrow it down.</p>
      ) : null}
      {fromCatalog.length ? (
        <>
          <h3 className="item-picker-heading">In the catalog</h3>
          <p className="faint">Picking one adds it to your inventory.</p>
          <ul className="picker-list">
            {fromCatalog.map((p, i) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={mine.length + i === marked ? "active" : ""}
                  disabled={adopt.isPending}
                  onClick={() => add(p)}
                >
                  <span>
                    {p.manufacturer ? `${p.manufacturer} ` : ""}
                    {p.name}
                  </span>
                  <span className="faint">
                    {[
                      categoryLabel(p.category),
                      p.hop ? HOP_FORM_LABELS[p.hop.form] : null,
                      p.origin,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <div>
        <button type="button" className="button" onClick={() => onCreate(typed.trim())}>
          <Plus size={16} /> New item
        </button>
      </div>
    </div>
  );
}
